import { BedDouble, Users, Maximize2, Eye, ArrowRight } from "lucide-react";
import Reveal from "./Reveal";
import { ROOMS, inr } from "../data/content";

const Spec = ({ icon: Icon, text }) => (
  <span className="flex items-center gap-1.5 text-[11px] tracking-wide text-[#A8A29E]">
    <Icon size={13} className="text-[#C5A059]" /> {text}
  </span>
);

const RoomCard = ({ room, onReserve, delay }) => (
  <Reveal delay={delay}>
    <article data-testid={`room-card-${room.id}`} className="group h-full border border-[#2A2421] bg-[#161311] transition-colors duration-500 hover:border-[#C5A059]/50">
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={room.image}
          alt={room.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0B0A]/60 to-transparent" />
        <span data-testid={`room-price-${room.id}`} className="absolute right-4 top-4 border border-[#C5A059]/50 bg-[#0D0B0A]/70 px-4 py-2 text-xs tracking-widest text-[#C5A059] backdrop-blur-md">
          {inr(room.price)} / night
        </span>
      </div>
      <div className="p-6 sm:p-7">
        <h3 className="font-display text-2xl text-[#F5F2EB]">{room.name}</h3>
        <p className="mt-2 text-sm leading-relaxed text-[#A8A29E]">{room.blurb}</p>
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
          <Spec icon={Maximize2} text={room.size} />
          <Spec icon={BedDouble} text={room.bed} />
          <Spec icon={Users} text={`${room.guests} Guests`} />
          <Spec icon={Eye} text={room.view} />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {room.perks.map((p) => (
            <span key={p} className="border border-[#2A2421] px-3 py-1 text-[10px] uppercase tracking-[0.15em] text-[#8C713B]">
              {p}
            </span>
          ))}
        </div>
        <button
          data-testid={`room-reserve-${room.id}`}
          onClick={() => onReserve(room.id)}
          className="mt-6 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C5A059] transition-all duration-300 hover:gap-4"
        >
          Reserve This Room <ArrowRight size={14} />
        </button>
      </div>
    </article>
  </Reveal>
);

const Rooms = ({ onReserve }) => {
  const [deluxe, premium, suite] = ROOMS;
  return (
    <section id="rooms" data-testid="rooms-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
      <Reveal>
        <div className="mb-14 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.35em] text-[#C5A059]">Stay With Us</p>
            <h2 className="mt-4 font-display text-4xl font-light text-[#F5F2EB] sm:text-5xl">Rooms & Suites</h2>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-[#A8A29E]">
            Every room is panelled in hand-carved Kashmiri walnut and finished with warm cove light — sample tariffs,
            confirmed personally by our front desk.
          </p>
        </div>
      </Reveal>

      <div className="grid gap-6 lg:grid-cols-2">
        <RoomCard room={deluxe} onReserve={onReserve} delay={0} />
        <RoomCard room={premium} onReserve={onReserve} delay={0.12} />
      </div>

      <Reveal delay={0.1} className="mt-6">
        <article data-testid="room-card-suite" className="group grid border border-[#2A2421] bg-[#161311] transition-colors duration-500 hover:border-[#C5A059]/50 lg:grid-cols-5">
          <div className="relative min-h-[320px] overflow-hidden lg:col-span-3">
            <img
              src={suite.image}
              alt={suite.name}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#161311]/40" />
            <div className="absolute bottom-5 left-5 hidden w-40 overflow-hidden border-2 border-[#C5A059]/60 sm:block">
              <img src={suite.image2} alt="Suite marble bathroom" loading="lazy" className="aspect-[4/5] w-full object-cover" />
            </div>
            <span data-testid="room-price-suite" className="absolute right-4 top-4 border border-[#C5A059]/50 bg-[#0D0B0A]/70 px-4 py-2 text-xs tracking-widest text-[#C5A059] backdrop-blur-md">
              {inr(suite.price)} / night
            </span>
          </div>
          <div className="flex flex-col justify-center p-7 sm:p-10 lg:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.35em] text-[#C5A059]">Signature</p>
            <h3 className="mt-3 font-display text-3xl text-[#F5F2EB]">{suite.name}</h3>
            <p className="mt-3 text-sm leading-relaxed text-[#A8A29E]">{suite.blurb}</p>
            <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2">
              <Spec icon={Maximize2} text={suite.size} />
              <Spec icon={BedDouble} text={suite.bed} />
              <Spec icon={Users} text={`${suite.guests} Guests`} />
              <Spec icon={Eye} text={suite.view} />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {suite.perks.map((p) => (
                <span key={p} className="border border-[#2A2421] px-3 py-1 text-[10px] uppercase tracking-[0.15em] text-[#8C713B]">
                  {p}
                </span>
              ))}
            </div>
            <button
              data-testid="room-reserve-suite"
              onClick={() => onReserve(suite.id)}
              className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C5A059] transition-all duration-300 hover:gap-4"
            >
              Reserve The Suite <ArrowRight size={14} />
            </button>
          </div>
        </article>
      </Reveal>
    </section>
  );
};

export default Rooms;
